import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8 md:p-12">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-[#3A5E63] font-serif mb-2">Privacy Policy</h1>
          <p className="text-sm text-[#344854]/70">Last updated: January 4, 2026</p>
        </div>

        <div className="prose prose-slate max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Introduction</h2>
            <p className="text-[#344854] leading-relaxed mb-4">
              At Healing Lotus Spa ("we," "our," or "us"), we respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, and safeguard your data when you use our website and services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Information We Collect</h2>
            <p className="text-[#344854] leading-relaxed mb-3">We collect information that you provide directly to us, including:</p>
            <ul className="list-disc list-inside text-[#344854] space-y-2 mb-4">
              <li><strong>Account Information:</strong> Name, email address, phone number, password</li>
              <li><strong>Profile Information:</strong> Preferred name, birth date, address, gender, pronouns, language preference</li>
              <li><strong>Appointment Information:</strong> Service bookings, appointment history, staff preferences</li>
              <li><strong>Payment Information:</strong> Payment card details (processed securely by Stripe), wallet balance, transaction history</li>
              <li><strong>Communication Data:</strong> Messages, feedback, and correspondence with us</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">How We Use Your Information</h2>
            <p className="text-[#344854] leading-relaxed mb-3">We use your information to:</p>
            <ul className="list-disc list-inside text-[#344854] space-y-2 mb-4">
              <li>Provide and manage your spa services and appointments</li>
              <li>Process payments and maintain your wallet balance</li>
              <li>Send appointment confirmations, reminders, and updates</li>
              <li>Communicate with you about our services, promotions, and special offers</li>
              <li>Improve our services and customer experience</li>
              <li>Comply with legal obligations and prevent fraud</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">How We Protect Your Information</h2>
            <p className="text-[#344854] leading-relaxed mb-4">
              We implement industry-standard security measures to protect your personal information:
            </p>
            <ul className="list-disc list-inside text-[#344854] space-y-2 mb-4">
              <li>All data is encrypted in transit and at rest</li>
              <li>Payment information is processed securely through Stripe (PCI-DSS compliant)</li>
              <li>Access to personal data is restricted to authorized personnel only</li>
              <li>Regular security audits and updates to our systems</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Third-Party Services</h2>
            <p className="text-[#344854] leading-relaxed mb-3">We use the following trusted third-party services:</p>
            <ul className="list-disc list-inside text-[#344854] space-y-2 mb-4">
              <li><strong>Stripe:</strong> Payment processing (credit cards, digital wallets)</li>
              <li><strong>Supabase:</strong> Database and authentication services</li>
              <li><strong>Email Service Provider:</strong> Appointment confirmations and notifications</li>
            </ul>
            <p className="text-[#344854] leading-relaxed">
              These third parties have their own privacy policies and we encourage you to review them. We do not sell or share your personal information with third parties for their marketing purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Cookies and Tracking</h2>
            <p className="text-[#344854] leading-relaxed mb-4">
              We use cookies and similar technologies to:
            </p>
            <ul className="list-disc list-inside text-[#344854] space-y-2 mb-4">
              <li>Keep you logged in to your account</li>
              <li>Remember your preferences and settings</li>
              <li>Analyze website traffic and usage patterns</li>
              <li>Improve our website performance and user experience</li>
            </ul>
            <p className="text-[#344854] leading-relaxed">
              You can control cookies through your browser settings, but disabling them may affect website functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Your Rights</h2>
            <p className="text-[#344854] leading-relaxed mb-3">You have the right to:</p>
            <ul className="list-disc list-inside text-[#344854] space-y-2 mb-4">
              <li><strong>Access:</strong> Request a copy of your personal information</li>
              <li><strong>Update:</strong> Correct or update your information through your profile settings</li>
              <li><strong>Delete:</strong> Request deletion of your account and personal data</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing emails at any time</li>
              <li><strong>Data Portability:</strong> Request your data in a portable format</li>
            </ul>
            <p className="text-[#344854] leading-relaxed">
              To exercise these rights, please contact us at the email address below.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Data Retention</h2>
            <p className="text-[#344854] leading-relaxed">
              We retain your personal information for as long as necessary to provide our services and comply with legal obligations. Appointment history and transaction records are kept for accounting and legal purposes. You may request deletion of your account at any time, subject to legal retention requirements.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Children's Privacy</h2>
            <p className="text-[#344854] leading-relaxed">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Changes to This Policy</h2>
            <p className="text-[#344854] leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through a notice on our website. Your continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-[#3A5E63] mb-4">Contact Us</h2>
            <p className="text-[#344854] leading-relaxed mb-4">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-[#F8F6F2] p-4 rounded-md">
              <p className="text-[#344854]"><strong>Healing Lotus Spa</strong></p>
              <p className="text-[#344854]">Email: privacy@lotushealingspa.com</p>
              <p className="text-[#344854]">Website: https://lotushealingspa.com</p>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-[#B2D8C5]">
          <Link
            to="/"
            className="inline-flex items-center text-[#3A5E63] hover:text-[#2d4a4d] transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
